import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, getObjectValues, Row, StyleProps } from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import {
	StudentMedicalEventCreateMutation,
	StudentMedicalEventDetailQuery,
	StudentMedicalEventUpdateMutation,
} from "../../../Utilities";
import { StudentMedicalEventFormState, StudentMedicalEventFormValues } from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<StudentMedicalEventCreateMutation["createStudentMedicalEvent"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentMedicalEventFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentMedicalEvent model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentMedicalEventFormValues.Create>;
	create: CreateFunction<StudentMedicalEventFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentMedicalEventFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentMedicalEventFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentMedicalEvent model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentMedicalEvent.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
					>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<StudentMedicalEventFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.Student.Select formState={formState} style={{ width: "100%" }} />
			<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
				<Field.CheckInTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
				<Field.CheckOutTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
			</Row>
			<Field.Description formState={formState} style={{ width: "100%" }} />
			<Field.Treatment formState={formState} style={{ width: "100%" }} />
			<Field.AttendingEmployees.Select formState={formState} style={{ width: "100%" }} />
			<Field.Outcome formState={formState} style={{ width: "100%" }} />
			<Field.Notes formState={formState} style={{ width: "100%" }} />
			<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentMedicalEventUpdateMutation["updateStudentMedicalEvent"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentMedicalEventFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentMedicalEvent model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		studentMedicalEvent: props.studentMedicalEvent,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentMedicalEvent: StudentMedicalEventDetailQuery["studentMedicalEvent"];
	formState: FormState<StudentMedicalEventFormValues.Detail>;
	update: UpdateFunction<StudentMedicalEventFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentMedicalEventFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentMedicalEventFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentMedicalEvent model using the given form state.
 */
export function Detail<T>({
	formState,
	studentMedicalEvent,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName =
		`studentMedicalEvent.detail.${studentMedicalEvent.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentMedicalEvent={studentMedicalEvent} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<StudentMedicalEventFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the StudentMedicalEvent model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, studentMedicalEvent: props.studentMedicalEvent });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentMedicalEvent: StudentMedicalEventDetailQuery["studentMedicalEvent"];
	formState: FormState<StudentMedicalEventFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentMedicalEvent model using the given form state.
 */
export function ReadOnly({ formState, studentMedicalEvent, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentMedicalEvent={studentMedicalEvent} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentMedicalEvent: StudentMedicalEventDetailQuery["studentMedicalEvent"];
	formState: FormState<StudentMedicalEventFormValues.Detail>;
};

function DetailFields({ formState, studentMedicalEvent }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.Student.Select
				formState={formState}
				currentInstance={studentMedicalEvent.student}
				style={{ width: "100%" }}
			/>
			<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
				<Field.CheckInTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
				<Field.CheckOutTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
			</Row>
			<Field.Description formState={formState} style={{ width: "100%" }} />
			<Field.Treatment formState={formState} style={{ width: "100%" }} />
			<Field.AttendingEmployees.Select
				formState={formState}
				currentInstances={studentMedicalEvent.attendingEmployees}
				style={{ width: "100%" }}
			/>
			<Field.Outcome formState={formState} style={{ width: "100%" }} />
			<Field.Notes formState={formState} style={{ width: "100%" }} />
			<MediaAttachmentCustomForm.ControlledMultiEditForm
				studentMedicalEventID={studentMedicalEvent.id}
				mediaAttachments={studentMedicalEvent.mediaAttachments}
			/>
			<Field.Author.Select
				formState={formState}
				currentInstance={studentMedicalEvent.author}
				style={{ width: "100%" }}
			/>
		</React.Fragment>
	);
}

export type CreateFieldsInStudentProps = {
	formState: FormState<StudentMedicalEventFormValues.CreateInStudent>;
};

export function CreateFieldsInStudent({ formState }: CreateFieldsInStudentProps) {
	return (
		<React.Fragment>
			<Field.CheckInTime formState={formState} />
			<Field.CheckOutTime formState={formState} />
			<Field.Description formState={formState} />
			<Field.Treatment formState={formState} />
			<Field.Outcome formState={formState} />
			<Field.Notes formState={formState} />
			<Field.MediaAttachments.SubForm formState={formState} />
			<Field.AttendingEmployees.Select formState={formState} />
		</React.Fragment>
	);
}
